<script setup lang="ts">
import { CheckCircle2, AlertCircle, AlertTriangle, Info, Loader2 } from '@lucide/vue'
import type { VNodeChild } from 'vue'
import RenderContent from '../internal/RenderContent'

export type MessageType = 'info' | 'success' | 'error' | 'warning' | 'loading'

const props = withDefaults(
  defineProps<{
    type?: MessageType
    content: VNodeChild
  }>(),
  {
    type: 'info'
  }
)
</script>

<template>
  <div class="rr-message" :class="[`rr-message--${type}`]" :role="type === 'error' ? 'alert' : 'status'">
    <span class="rr-message__icon">
      <CheckCircle2 v-if="type === 'success'" :size="16" class="icon-success" aria-hidden="true" />
      <AlertCircle v-else-if="type === 'error'" :size="16" class="icon-error" aria-hidden="true" />
      <AlertTriangle v-else-if="type === 'warning'" :size="16" class="icon-warning" aria-hidden="true" />
      <Loader2 v-else-if="type === 'loading'" :size="16" class="icon-loading is-spinning" aria-hidden="true" />
      <Info v-else :size="16" class="icon-info" aria-hidden="true" />
    </span>
    <span class="rr-message__content"><RenderContent :value="content" /></span>
  </div>
</template>

<style scoped>
.rr-message {
  max-width: 100%;
  overflow-wrap: anywhere;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: var(--rr-color-surface, #ffffff);
  border: 1px solid var(--rr-color-border, #e1e5ea);
  border-radius: var(--rr-radius-panel, 8px);
  box-shadow: var(--rr-shadow-2, 0 4px 12px rgba(15, 23, 42, 0.1));
  color: var(--rr-color-text-strong, #171a1f);
  font-size: var(--rr-font-size-base, 13px);
  line-height: 1.4;
  pointer-events: all;
  box-sizing: border-box;
  margin-bottom: 8px;
  animation: rr-message-move-in 200ms cubic-bezier(0.2, 0, 0, 1);
}

.rr-message__icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.icon-success {
  color: var(--rr-color-success, #157a55);
}

.icon-error {
  color: var(--rr-color-danger, #be3038);
}

.icon-warning {
  color: var(--rr-color-warning, #946008);
}

.icon-info {
  color: var(--rr-color-info, #1667d9);
}

.icon-loading {
  color: var(--rr-color-primary, #5a46e0);
}

.is-spinning {
  animation: rr-spin 1s linear infinite;
}

@keyframes rr-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes rr-message-move-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .rr-message, .is-spinning { animation: none; }
}
</style>
